import React from "react";
import { Bookmark } from "lucide-react";

type BookmarkShapeCardProps = {
  title: string;
  badge?: string | null;
  hoverLabel?: string | null;
  footer: React.ReactNode;
  isActive?: boolean;
  accentColor?: string;
  isBadgeMuted?: boolean;
  onClick?: () => void;
  tintBackgroundOnMobile?: boolean;
  disableHoverOnMobile?: boolean;
};

const withHexAlpha = (color: string, alphaHex: string) =>
  /^#[\da-fA-F]{6}$/.test(color) ? `${color}${alphaHex}` : color;

const CARD_SIZE_CLASS = "h-full min-h-[10.75rem] sm:min-h-[11.5rem]";

const BookmarkShapeCard: React.FC<BookmarkShapeCardProps> = ({
  title,
  badge,
  hoverLabel,
  footer,
  isActive = false,
  accentColor = "#f6851f",
  isBadgeMuted = false,
  onClick,
  tintBackgroundOnMobile = false,
  disableHoverOnMobile = false,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative block w-full overflow-hidden rounded-[22px] border bg-[linear-gradient(180deg,#ffffff,#f8fafc)] text-left transition duration-200 ease-out ${CARD_SIZE_CLASS} ${
        isActive ? "" : "border-slate-200"
      }`}
      style={
        isActive
          ? {
              borderColor: accentColor,
              boxShadow: `0 0 0 1px ${accentColor} inset`,
            }
          : undefined
      }
    >
      {tintBackgroundOnMobile ? (
        <div
          className="pointer-events-none absolute inset-0 z-[1] rounded-[22px] sm:hidden"
          style={{ backgroundColor: accentColor, opacity: 0.1 }}
        />
      ) : null}

      <div className="relative z-10 flex h-full flex-col px-4 pb-4 pt-4 transition duration-200 ease-out sm:px-5 sm:pb-5">
        <div className="flex flex-col">
          <p className="min-h-[2.15rem] break-words text-sm font-semibold leading-[1.1rem] tracking-[-0.04em] text-slate-900 line-clamp-2 sm:text-[0.95rem]">
            {title}
          </p>
        </div>

        <div className="flex flex-1 items-center justify-center py-3">
          {badge ? (
            <div className="flex flex-col items-center justify-center gap-1.5 text-center">
              <Bookmark
                size={34}
                strokeWidth={1.8}
                className={isBadgeMuted ? "text-slate-400" : "text-slate-950"}
                aria-hidden="true"
              />
              <span
                className={`text-sm font-semibold uppercase ${
                  isBadgeMuted ? "text-slate-400" : "text-slate-950"
                }`}
              >
                {badge}
              </span>
            </div>
          ) : null}
        </div>

        <div className="pt-1.5 text-xs leading-4 text-slate-600 sm:text-[0.8rem]">
          {footer}
        </div>
      </div>

      <div
        className={`pointer-events-none absolute inset-0 z-20 rounded-[22px] opacity-0 transition duration-200 ease-out ${
          disableHoverOnMobile
            ? "hidden sm:block sm:group-hover:opacity-100 sm:group-focus-visible:opacity-100"
            : "group-hover:opacity-100 group-focus-visible:opacity-100"
        }`}
        style={{ backgroundColor: withHexAlpha(accentColor, "50") }}
      >
        <div className="flex h-full flex-col px-4 pb-4 pt-4 sm:px-5 sm:pb-5">
          <div className="flex flex-col opacity-0" aria-hidden="true">
            <p className="min-h-[2.15rem] break-words text-sm font-semibold leading-[1.1rem] tracking-[-0.04em] line-clamp-2 sm:text-[0.95rem]">
              {title}
            </p>
          </div>

          <div className="flex flex-1 items-center justify-center py-3">
            <div className="flex flex-col items-center justify-center gap-1.5">
              <Bookmark
                size={34}
                strokeWidth={1.8}
                className="text-slate-950"
                aria-hidden="true"
              />
              {hoverLabel ? (
                <span className="text-sm font-semibold uppercase text-slate-950">
                  Strona {hoverLabel}
                </span>
              ) : null}
            </div>
          </div>

          <div
            className="pt-1.5 text-xs leading-4 opacity-0 sm:text-[0.8rem]"
            aria-hidden="true"
          >
            {footer}
          </div>
        </div>
      </div>
    </button>
  );
};

export default BookmarkShapeCard;
